import { useEffect, useState } from 'react'
import { Card, Icon, Table } from '../../gsys-ui'
import haxios from '../../lib/haxios'
import useApi from '../../hooks/useApi'
import moment from 'moment'
import { ClockIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, UsersIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { Checkbox, NativeSelect, TextInput, Button, ActionIcon, Textarea, Group, Radio } from '@mantine/core'
import { DateInput, DateTimePicker } from '@mantine/dates'
import { modals } from '@mantine/modals'
import { errorToast, successToast } from '../../lib/helpers'
import { MdOutlineSick } from 'react-icons/md'

const SickDays = () => {
  const [startDate, setStartDate] = useState(moment().subtract(1, 'month').toDate())
  const [endDate, setEndDate] = useState(new Date())

  const { data, isLoading, refetch } = useApi({
    url: 'https://api.aa-vision.com/api/tma/sickdays/',
    method: 'post',
    data: {
      Branch: null,
      StaffPosition: null,
      StartDate: moment(startDate).format('YYYY-MM-DD'),
      EndDate: moment(endDate).format('YYYY-MM-DD')
    }
  })
  const [searchValue, setSearchValue] = useState('')

  useEffect(() => {
    refetch()
  }, [startDate, endDate])

  if (isLoading) return null

  const handleAddSickDay = () => {
    modals.openContextModal({
      modal: 'sickDayEditModal',
      title: 'Add a sick day record',
      centered: true,
      innerProps: {

      }
    })
  }

  const dataFiltered = data.filter((el) =>
    `${el.FName} ${el.SName} ${el.Branch}`
      .toLowerCase()
      .includes(searchValue.toLowerCase())
  )

  return (
    <Card>
      <Card.Title>
        <div className="flex items-center space-x-2">
          <Icon Comp={MdOutlineSick} />
          <div>Sick Days</div>
        </div>
        <div className="flex items-center space-x-2">
          <div>From</div>
          <DateInput value={startDate} onChange={setStartDate} />
          <div>To</div>
          <DateInput value={endDate} onChange={setEndDate} />
          <Button onClick={handleAddSickDay}>Add record</Button>
        </div>
      </Card.Title>
      <Table
        data={dataFiltered}
        columns={[
          {
            header: 'Name',
            id: 'name',
            cell: ({ row }) => `${row.original.FName} ${row.original.SName}`
          },
          {
            header: 'Position',
            accessorKey: 'Position'
          },
          {
            header: 'Branch',
            accessorKey: 'StaffBranch'
          },
          {
            header: 'Start',
            id: 'start',
            cell: ({ row }) => row.original.StartDateTime ? (
              moment(row.original.StartDateTime).format('DD/MM/YY HH:mm')
            ) : (
              '-'
            )
          },
          {
            header: 'End',
            id: 'end',
            cell: ({ row }) => row.original.EndDateTime ? (
              moment(row.original.EndDateTime).format('DD/MM/YY HH:mm')
            ) : (
              '-'
            )
          },
          {
            header: 'Paid',
            id: 'paid',
            cell: ({ row }) => row.original.Paid ? (
              'Paid'
            ) : (
              'Unpaid'
            )
          },
          {
            header: 'Days',
            accessorKey: 'TotalDays'
          },
          {
            header: 'Reason',
            accessorKey: 'Reason'
          },
          {
            header: 'Actions',
            id: 'edit',
            width: 1,
            cell: ({ row }) => <Actions row={row.original} refetch={refetch} />
          },
        ]}
      />
    </Card>
  )
}

const Actions = ({ row, refetch }) => {
  const handleDelete = (id) => {
    modals.openConfirmModal({
      title: 'Are you sure?',
      centered: true,
      confirmProps: { color: 'red' },
      children: (
        <div className="text-center">
          <Icon Comp={XCircleIcon} size={20} className="mb-4 text-red-500" />
          <div>Are you sure you want to delete this sick day record?</div>
          <div>You are deleting the record for <span className="font-bold">{row.FName} {row.SName}</span></div>
          <div>
            Between <span className="font-bold">{moment(row.StartDateTime).format('DD/MM/YYYY')} </span>
            and <span className="font-bold">{moment(row.EndDateTime).format('DD/MM/YYYY')}</span>
          </div>
          <div className="font-bold">This action cannot be undone!</div>
        </div>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      onCancel: () => { },
      onConfirm: async () => {
        try {
          await haxios.delete(`https://api.aa-vision.com/api/tma/sickday/${row.SickID}`)
          await refetch()
        } catch (e) {
          errorToast('An error has occurred.')
          console.log(e)
          return
        }

        successToast('Deleted sick day record.')
      }
    })
  }

  const handleEdit = () => {
    modals.openContextModal({
      modal: 'sickDayEditModal',
      title: 'Edit a sick day record',
      centered: true,
      innerProps: {

      }
    })
  }

  return (
    <div className="flex space-x-1">
      <ActionIcon variant="subtle" color="gray" onClick={handleEdit}>
        <Icon Comp={PencilIcon} size={4.5} />
      </ActionIcon>
      <ActionIcon variant="subtle" color="gray" onClick={handleDelete}>
        <Icon Comp={TrashIcon} size={4.5} />
      </ActionIcon>
    </div>
  )
}

export const SickDayEditModal = ({ context, id, innerProps }) => {
  const { register, handleSubmit, formState: { errors } } = useForm()

  const onSubmit = (data) => console.log(data)

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <NativeSelect label="Staff member" {...register('StaffID')} />
        <Checkbox label="Paid" />
        <TextInput label="Paid hours per day" {...register('HoursPerDay')} />
        <div className="font-bold">Total hours to be paid: todo</div>
        <Textarea label="Reason" {...register('Reason')} />
        <DateInput label="Start date" {...register('StartDateTime')} />
        <DateInput label="End date (inclusive)" {...register('EndDateTime')} />
        <Group>
          <Radio label="All day" />
          <Radio label="AM" />
          <Radio label="PM" />
        </Group>
        <Button>Submit</Button>
      </form>
    </>
  )
}

export default SickDays